import React from "react";
import { HistoryIcon } from "../atoms/HistoryIcon";
import { UserTimestamp } from "../atoms/UserTimestamp";
import { ChangeValue } from "../atoms/ChangeValue";
import { getActionLabel, getFieldLabel } from "../historyUtils";
import dayjs from "dayjs";
import {
  ItemContainer,
  IconColumn,
  ContentColumn,
  ChangesWrapper,
  ContentHeader,
  SummaryBox,
  ChipRow,
  Chip,
} from "../index.styles";

/**
 * Renders a single activity log item in the timeline.
 * @param {Object} props
 * @param {Object} props.item - The history event data.
 * @param {boolean} props.isLast - Whether this is the last item in the timeline.
 * @return {JSX.Element}
 */
export const TimelineItem = ({ item, isLast }) => {
  const {
    history_type,
    history_user,
    history_date,
    changes,
    title,
    summary,
    context = {},
  } = item;

  const actionLabel = title || getActionLabel(history_type);
  const formattedDate = dayjs(history_date).format("D MMM YYYY, h:mm A");

  // Extract user name safely - assuming history_user might be an object or string
  let userName = "System";
  if (history_user) {
    if (typeof history_user === "string") userName = history_user;
    else if (history_user.name) userName = history_user.name;
    else if (history_user.first_name)
      userName = `${history_user.first_name} ${history_user.last_name || ""}`.trim();
    else if (history_user.email) userName = history_user.email;
  } else if (item.source) {
    userName = item.source.charAt(0).toUpperCase() + item.source.slice(1);
  }

  const productNumber = context?.product_number;
  const productName = context?.product_name;
  const lineNumber = context?.line_number;
  
  const showProductChip = Boolean(productNumber || productName);
  const showLineChip = Boolean(lineNumber);
  const hasChips = showProductChip || showLineChip;

  return (
    <ItemContainer>
      <IconColumn $isLast={isLast}>
        <HistoryIcon type={history_type} />
      </IconColumn>
      <ContentColumn>
        <ContentHeader>{title}</ContentHeader>

        {hasChips && (
          <ChipRow>
            {showProductChip && <Chip>{productNumber || productName}</Chip>}
            {showLineChip && <Chip>Line {lineNumber}</Chip>}
          </ChipRow>
        )}

        {history_type === "~" && changes?.length > 0 && (
          <ChangesWrapper>
            {changes.map((change, index) => (
              <ChangeValue
                key={change.field || index}
                field={change.label || getFieldLabel(change.field)}
                fieldName={change.field}
                oldValue={change.old_display !== undefined ? change.old_display : change.old}
                newValue={change.new_display !== undefined ? change.new_display : change.new}
                actionType={history_type}
              />
            ))}
          </ChangesWrapper>
        )}

        {summary && (history_type !== "~" || !changes?.length || !hasChips) && (
          <SummaryBox>{summary}</SummaryBox>
        )}

        <UserTimestamp userName={userName} action={actionLabel} timestamp={formattedDate} />
      </ContentColumn>
    </ItemContainer>
  );
};
